import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { track } from '@amplitude/analytics-browser'
import {
  Button,
  PopoverPanel,
  SectionHeading,
  TextField,
  Toggle,
  colors,
} from 'src/components/app-ui'
import AccessRule from 'src/model/access-rule'
import { domainListAction } from 'src/reducers/domain/action'
import { LINK_CONTEXTS, getPathLink } from 'src/utils/portal-links'
import usePortal from 'src/hooks/usePortal'
import useCatalogListing from 'src/hooks/useCatalogListing'

const DEFAULT_DOMAIN =
  process.env.REACT_APP_VIEW_URL?.replace(/^https?:\/\//, '').replace(
    /\/$/,
    '',
  ) || 'share.minicoursegenerator.com'

const DOMAIN_QUERY = {
  CurrentPage: 0,
  PageSize: 200,
  SortingColumn: 'LastModifiedAt',
  Asc: false,
  FilterText: '',
  Filters: [],
}

const useStyles = makeStyles({
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px',
  },
  domainButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    color: colors.navy,
  },
  domainList: {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px',
  },
  domainOption: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    border: 0,
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: '13px',
    '&:hover': {
      background: colors.hover,
    },
  },
  domainKind: {
    fontSize: '11px',
    fontWeight: 500,
    color: colors.subtle,
  },
  domainSetup: {
    marginTop: 4,
    padding: '8px 12px',
    border: 0,
    borderTop: '1px solid #F1F3F5',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: '12px',
    color: colors.primary,
  },
  linkCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '14px 16px',
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
  },
  linkTitle: {
    fontSize: '14px',
    fontWeight: 600,
  },
  linkRow: {
    display: 'flex',
    gap: '8px',
  },
  linkHint: {
    fontSize: '12px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  badge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '.06em',
  },
  linkTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  catalog: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  catalogTitle: {
    fontSize: '14px',
    fontWeight: 500,
  },
  catalogDesc: {
    fontSize: '12px',
    color: colors.muted,
  },
  copied: {
    background: `${colors.successDark} !important`,
  },
  access: {
    fontSize: '12px',
    color: colors.subtle,
  },
  strong: {
    fontWeight: 600,
    color: colors.navy,
  },
})

/** Shareable link of the path, on the default or a verified custom domain. */
const LinksSection = ({ collection }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const domainState = useSelector(state => state.domain.list)
  const [domain, setDomain] = useState(DEFAULT_DOMAIN)
  const [menuOpen, setMenuOpen] = useState(false)
  const [copiedContext, setCopiedContext] = useState(null)
  const [showShareButtons, setShowShareButtons] = useState(false)
  const [listedInCatalog, setListedInCatalog] = useCatalogListing(
    'path',
    collection,
  )
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  // Where portal links point: the workspace portal's domain, else
  // share…/portal/paths/{id}. The domain picker only changes the direct link.
  const portal = usePortal(collection?.shopKey)

  // Fetched on every mount: the shared list can hold one page of 5 from the custom domain settings.
  useEffect(() => {
    dispatch(domainListAction(DOMAIN_QUERY, () => {}))
  }, [dispatch])

  // Every verified custom domain of the workspace. One whose default path is this one opens it at
  // its root; the others serve it at /category/….
  const customDomains = useMemo(
    () =>
      (domainState?.results || []).filter(
        item => Number(item.verificationStatus) === 1,
      ),
    [domainState],
  )
  const opensPath = useCallback(
    item => String(item.defaultCollectionDto?.id) === String(collection.id),
    [collection.id],
  )

  // A domain that opens this path is picked first.
  useEffect(() => {
    const own = customDomains.find(opensPath)
    if (own) setDomain(own.domain)
  }, [customDomains, opensPath])

  const domains = [
    { label: DEFAULT_DOMAIN, kind: 'Default' },
    ...customDomains.map(item => ({
      label: item.domain,
      kind: opensPath(item) ? 'Path domain' : 'Custom domain',
      root: opensPath(item),
    })),
  ]

  const linkFor = context =>
    `${getPathLink({
      context,
      domain,
      defaultDomain: DEFAULT_DOMAIN,
      path: collection,
      portal,
      root: !!domains.find(item => item.label === domain)?.root,
    })}${showShareButtons ? '?shr=1' : ''}`

  const copy = context => {
    track('collection_share_link_copied', { link_context: context })
    navigator.clipboard?.writeText(linkFor(context))
    setCopiedContext(context)
    setTimeout(() => setCopiedContext(null), 2000)
  }

  const LINK_CARDS = [
    {
      context: LINK_CONTEXTS.PORTAL,
      title: 'Portal link',
      badge: 'IN PORTAL',
      badgeStyle: { color: colors.primary, background: colors.primarySoft },
      hint:
        'Opens the path inside your portal, with its header, navigation and catalog. Use it for learners who already use the portal.',
    },
    {
      context: LINK_CONTEXTS.DIRECT,
      title: 'Direct link',
      badge: 'STANDALONE',
      badgeStyle: { color: colors.slate, background: colors.chip },
      hint:
        'Only this path, with no portal navigation. Use it in emails and campaigns. Widgets use this link too.',
    },
  ]

  return (
    <>
      <div className={classes.head}>
        <SectionHeading
          title="Share this learning path"
          description="Two links, same path. Pick based on where learners come from."
        />
        <PopoverPanel
          open={menuOpen}
          onClose={closeMenu}
          width={280}
          align="right"
          panelStyle={{ top: '100%', marginTop: 4, borderRadius: 8 }}
          trigger={
            <button
              type="button"
              className={classes.domainButton}
              onClick={() => setMenuOpen(open => !open)}
            >
              <Icon icon="tabler:world" width={13} height={13} />
              {domain}
              <Icon
                icon="tabler:chevron-down"
                width={12}
                height={12}
                color={colors.muted}
              />
            </button>
          }
        >
          <div className={classes.domainList}>
            {domains.map(item => (
              <button
                key={item.label}
                type="button"
                className={classes.domainOption}
                style={{
                  background:
                    item.label === domain
                      ? 'rgba(1,95,251,.08)'
                      : 'transparent',
                  color: item.label === domain ? colors.primary : colors.navy,
                }}
                onClick={() => {
                  setDomain(item.label)
                  closeMenu()
                }}
              >
                {item.label}
                <span className={classes.domainKind}>{item.kind}</span>
              </button>
            ))}
            <button
              type="button"
              className={classes.domainSetup}
              onClick={() => history.push('/workspace/custom-domain')}
            >
              Set up a custom domain
            </button>
          </div>
        </PopoverPanel>
      </div>

      {LINK_CARDS.map(card => {
        const isCopied = copiedContext === card.context
        const url = linkFor(card.context)
        return (
          <div key={card.context} className={classes.linkCard}>
            <div className={classes.linkTitleRow}>
              <span className={classes.linkTitle}>{card.title}</span>
              <span className={classes.badge} style={card.badgeStyle}>
                {card.badge}
              </span>
            </div>
            <div className={classes.linkRow}>
              <TextField readOnly value={url} />
              <Button
                size="small"
                variant="secondary"
                icon="tabler:external-link"
                title="Open in new tab"
                onClick={() => window.open(url, '_blank')}
              />
              <Button
                size="small"
                icon={isCopied ? 'tabler:check' : 'tabler:copy'}
                className={isCopied ? classes.copied : undefined}
                onClick={() => copy(card.context)}
              >
                {isCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div className={classes.linkHint}>{card.hint}</div>
          </div>
        )
      })}

      <div className={classes.catalog}>
        <div>
          <div className={classes.catalogTitle}>Show in portal catalog</div>
          <div className={classes.catalogDesc}>
            Turn off to keep this path reachable only by direct link.
          </div>
        </div>
        <Toggle checked={listedInCatalog} onChange={setListedInCatalog} />
      </div>

      <div className={classes.catalog}>
        <div>
          <div className={classes.catalogTitle}>Show share buttons</div>
          <div className={classes.catalogDesc}>
            Adds social share buttons to the page opened from this link.
          </div>
        </div>
        <Toggle checked={showShareButtons} onChange={setShowShareButtons} />
      </div>

      <div className={classes.access}>
        Access follows the path's authentication:{' '}
        <strong className={classes.strong}>
          {AccessRule[collection.accessRule || 0]}
        </strong>
        .
      </div>
    </>
  )
}

export default LinksSection
